import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Text, Spinner } from 'grommet';
import { Add, Subtract } from 'grommet-icons';
import { authorizedFetch, capitalize, getSourceInfo } from '../../../utils';
import { transformVerticalsData } from '../../../components/filters/vertical-subvertical-selector';
import { addError } from '../../../toasts/toasts.actions';
import { getGeoNameEntries } from '../../../components/filters/location-selector';
import { MEETS } from '../../../components/filters/filter-utils';
import {
  AVAILABLE_DEVICE_FIELDS,
  getPartnerProperties,
  getProfileProperties,
  PROFILE_FIELDS
} from '../../../components/filters/session-attributes-selector';
import { toDisplayValue } from '../../../profile-attributes/profile-attributes.constants';


const OPERATORS_OPTIONS = [
  { label: 'Equals', value: 'Equal' },
  { label: 'Does Not Equal', value: 'NotEqual' },
  { label: 'Greater Than', value: 'GreaterThan' },
  { label: 'Greater Than Or Equal To', value: 'GreaterThanOrEqual' },
  { label: 'Less Than', value: 'LessThan' },
  { label: 'Less Than Or Equal To', value: 'LessThanOrEqual' },
  { label: 'Exists', value: 'Exists' },
  { label: 'Does Not Exist', value: 'DoesNotExist' },
  { label: 'Is One Of', value: 'In' },
  { label: 'Is Not One Of', value: 'NotIn' }
];

const getProfileAttributes = async () => {
  try {
    return await authorizedFetch(`/api/Targeting/targetingAttributes`);
  } catch (e) {
    addError('Error loading profile attributes', e.message);
    return [];
  }
};

const TargetingRule = React.memo(({ label, values, matchMode }) => {
  if (!values?.length) return null;

  return (
    <Box direction='row' gap='xsmall'>
      <Text weight='bold'>{label}:</Text>
      <Box direction='row' wrap gap='xsmall' flex>
        {values.map((value, index) => (
          <React.Fragment key={index}>
            <Text>{value}</Text>
            {index < values.length - 1 && (
              <Text weight='bold' margin={{ left: 'xsmall' }}>
                {matchMode
                  ? MEETS.find(m => m.value === matchMode)?.label
                  : 'OR'}
              </Text>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
});

const TargetingSection = React.memo(({ title, rules }) => {
  const hasRules = useMemo(
    () =>
      rules && Object.keys(rules).some(key => rules[key]?.values?.length > 0),
    [rules]
  );

  if (!hasRules) return null;

  return (
    <Box gap='xsmall'>
      <Box direction='row' align='center' gap='xsmall'>
        {title === 'Include' ? (
          <Add size='small' color='status-ok' />
        ) : (
          <Subtract size='small' color='status-critical' />
        )}
        <Text
          weight='bold'
          size='medium'
          color={title === 'Include' ? 'status-ok' : 'status-critical'}
        >
          {title}
        </Text>
      </Box>

      {Object.entries(rules).map(
        ([key, rule]) =>
          rule?.values?.length > 0 && (
            <TargetingRule
              key={key}
              label={
                key === 'sourceIds'
                  ? 'Source IDs'
                  : key === 'verticals'
                  ? 'Vertical & Subvertical'
                  : key === 'locations'
                  ? 'Locations'
                  : key === 'postalCodes'
                  ? 'Zip/Postal Codes'
                  : key === 'profileAttributes'
                  ? 'Profile Attributes'
                  : key === 'sessionAttributes'
                  ? 'Session Attributes'
                  : key
              }
              values={rule.values}
              matchMode={rule.matchMode}
            />
          )
      )}
    </Box>
  );
});

const AdGroupDetails = ({ data }) => {
  const [state, setState] = useState({
    sourceIds: {},
    verticalSubvertical: {},
    geoNameEntries: {},
    profileAttributes: [],
    partnerAttributes: [],
    profileProperties: [],
    isLoading: true
  });

  const fetchData = useCallback(async () => {
    const targeting = data?.adGroup?.targeting;
    if (!targeting) return;

    const promises = [];

    // Fetch source IDs
    if (
      targeting.sourceIds?.include?.length ||
      targeting.sourceIds?.exclude?.length
    ) {
      promises.push(
        getSourceInfo()
          .then(sourceInfoData =>
            setState(prev => ({ ...prev, sourceIds: sourceInfoData }))
          )
          .catch(() => {})
      );
    }

    // Fetch vertical subvertical
    if (
      targeting.verticals?.include?.length ||
      targeting.verticals?.exclude?.length
    ) {
      promises.push(
        authorizedFetch(`/api/vertical/getAll`)
          .then(response => {
            const transformedData = transformVerticalsData(response);
            const result = transformedData.reduce((acc, item) => {
              acc[item.id] = item.verticalName;
              return acc;
            }, {});
            setState(prev => ({ ...prev, verticalSubvertical: result }));
          })
          .catch(e => addError('Error lookup Vertical data', e.message))
      );
    }

    // Fetch geo name entries
    if (
      targeting.locations?.include?.length ||
      targeting.locations?.exclude?.length
    ) {
      const combinedLocations = [
        ...(targeting.locations.include || []),
        ...(targeting.locations.exclude || [])
      ];
      promises.push(
        getGeoNameEntries(combinedLocations, entries =>
          setState(prev => ({ ...prev, geoNameEntries: entries }))
        )
      );
    }

    // Fetch profile attributes
    if (
      targeting.profileAttributeConditions?.include?.length ||
      targeting.profileAttributeConditions?.exclude?.length
    ) {
      promises.push(
        getProfileAttributes().then(attributes =>
          setState(prev => ({ ...prev, profileAttributes: attributes }))
        )
      );
    }

    // Fetch partner attributes
    if (
      targeting.sessionAttributes?.partnerAttributes?.include?.length ||
      targeting.sessionAttributes?.partnerAttributes?.exclude?.length
    ) {
      promises.push(
        getPartnerProperties(attributes =>
          setState(prev => ({ ...prev, partnerAttributes: attributes }))
        )
      );
    }

    // Fetch profile properties
    if (
      targeting.sessionAttributes?.profile?.include?.length ||
      targeting.sessionAttributes?.profile?.exclude?.length
    ) {
      promises.push(
        getProfileProperties(
          properties =>
            setState(prev => ({ ...prev, profileProperties: properties })),
          PROFILE_FIELDS
        )
      );
    }

    await Promise.all(promises);
    setState(prev => ({ ...prev, isLoading: false }));
  }, [data]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatTargetingRules = useCallback(() => {
    const targeting = data?.adGroup?.targeting;
    if (!targeting) return { includeRules: {}, excludeRules: {} };

    const formatRule = (condition, lookup) => {
      const name = lookup?.find(attr => attr.fieldKey === condition.key)?.name;
      const strategy =
        OPERATORS_OPTIONS?.find(op => op.value === condition.comparisonStrategy)
          ?.label || capitalize(condition.comparisonStrategy);
      const values = condition.values
        .map(v => toDisplayValue(v))
        .join(', ');
      return `${name} ${
        condition.comparisonInverse ? 'Does Not Exist' : strategy
      } ${values}`;
    };

    const includeRules = {};
    const excludeRules = {};

    // Format Source IDs
    if (targeting.sourceIds?.include) {
      includeRules.sourceIds = {
        values: targeting.sourceIds.include.map(id =>
          state.sourceIds[id]?.name ? `${state.sourceIds[id].name} | ${id}` : id
        )
      };
    }
    if (targeting.sourceIds?.exclude) {
      excludeRules.sourceIds = {
        values: targeting.sourceIds.exclude.map(id =>
          state.sourceIds[id]?.name ? `${state.sourceIds[id].name} | ${id}` : id
        )
      };
    }

    // Format Verticals
    if (targeting.verticals?.include) {
      includeRules.verticals = {
        values: targeting.verticals.include.map(
          id => state.verticalSubvertical[id] || id
        ),
        matchMode: targeting.verticals.includeMatchMode
      };
    }
    if (targeting.verticals?.exclude) {
      excludeRules.verticals = {
        values: targeting.verticals.exclude.map(
          id => state.verticalSubvertical[id] || id
        ),
        matchMode: targeting.verticals.excludeMatchMode
      };
    }

    // Format Locations
    if (targeting.locations?.include) {
      includeRules.locations = {
        values: targeting.locations.include.map(
          id => state.geoNameEntries[id]?.name || id
        ),
        matchMode: targeting.locations.includeMatchMode
      };
    }
    if (targeting.locations?.exclude) {
      excludeRules.locations = {
        values: targeting.locations.exclude.map(
          id => state.geoNameEntries[id]?.name || id
        ),
        matchMode: targeting.locations.excludeMatchMode
      };
    }

    // Format Postal Codes
    const formatPostalCodes = (codes, limit) => {
      const displayCodes = codes.slice(0, limit);
      const remainingCount = codes.length - limit;
      return remainingCount > 0
        ? [...displayCodes, `${remainingCount} additional ZIP/postal codes`]
        : displayCodes;
    };

    if (targeting.postalCodes?.include) {
      includeRules.postalCodes = {
        values: formatPostalCodes(targeting.postalCodes.include, 5)
      };
    }
    if (targeting.postalCodes?.exclude) {
      excludeRules.postalCodes = {
        values: formatPostalCodes(targeting.postalCodes.exclude, 10)
      };
    }

    // Format Profile Attribute Conditions
    if (targeting.profileAttributeConditions?.include) {
      includeRules.profileAttributes = {
        values: targeting.profileAttributeConditions.include.map(condition =>
          formatRule(condition, state.profileAttributes)
        ),
        matchMode: targeting.profileAttributeConditions.includeMatchMode
      };
    }
    if (targeting.profileAttributeConditions?.exclude) {
      excludeRules.profileAttributes = {
        values: targeting.profileAttributeConditions.exclude.map(condition =>
          formatRule(condition, state.profileAttributes)
        ),
        matchMode: targeting.profileAttributeConditions.excludeMatchMode
      };
    }

    // Format Session Attributes
    if (targeting.sessionAttributes) {
      const formatSessionAttribute = (condition, lookup, prefix = '') => {
        const name =
          lookup?.find(attr => attr.field === condition.key)?.label ||
          lookup?.find(attr => attr.field === condition.key)?.displayName;
        const strategy =
          OPERATORS_OPTIONS?.find(
            op => op.value === condition.comparisonStrategy
          )?.label || capitalize(condition.comparisonStrategy);
        const inverse = condition.comparisonInverse ? 'Does Not Exist' : '';
        const values = condition.values.join(', ');
        return inverse
          ? `${prefix || name} ${inverse}`
          : `${prefix || name} ${strategy} ${values}`;
      };

      const includeSessionAttributeRules = [];
      const excludeSessionAttributeRules = [];

      // Format Device Conditions
      if (targeting.sessionAttributes.device?.include) {
        includeSessionAttributeRules.push(
          ...targeting.sessionAttributes.device.include.map(condition =>
            formatSessionAttribute(condition, AVAILABLE_DEVICE_FIELDS)
          )
        );
      }
      if (targeting.sessionAttributes.device?.exclude) {
        excludeSessionAttributeRules.push(
          ...targeting.sessionAttributes.device.exclude.map(condition =>
            formatSessionAttribute(condition, AVAILABLE_DEVICE_FIELDS)
          )
        );
      }

      // Format Query Parameters
      if (targeting.sessionAttributes.queryParams?.include) {
        includeSessionAttributeRules.push(
          ...targeting.sessionAttributes.queryParams.include.map(condition => {
            const paramName = condition.key.split('queryParams.')[1];
            return formatSessionAttribute(condition, null, `${paramName} `);
          })
        );
      }
      if (targeting.sessionAttributes.queryParams?.exclude) {
        excludeSessionAttributeRules.push(
          ...targeting.sessionAttributes.queryParams.exclude.map(condition => {
            const paramName = condition.key.split('queryParams.')[1];
            return formatSessionAttribute(condition, null, `${paramName} `);
          })
        );
      }

      // Format Partner Attributes
      if (targeting.sessionAttributes.partnerAttributes?.include) {
        includeSessionAttributeRules.push(
          ...targeting.sessionAttributes.partnerAttributes.include.map(
            condition =>
              formatSessionAttribute(condition, state.partnerAttributes)
          )
        );
      }
      if (targeting.sessionAttributes.partnerAttributes?.exclude) {
        excludeSessionAttributeRules.push(
          ...targeting.sessionAttributes.partnerAttributes.exclude.map(
            condition =>
              formatSessionAttribute(condition, state.partnerAttributes)
          )
        );
      }

      // Format Profile Attributes
      if (targeting.sessionAttributes.profile?.include) {
        includeSessionAttributeRules.push(
          ...targeting.sessionAttributes.profile.include.map(condition =>
            formatSessionAttribute(condition, state.profileProperties)
          )
        );
      }
      if (targeting.sessionAttributes.profile?.exclude) {
        excludeSessionAttributeRules.push(
          ...targeting.sessionAttributes.profile.exclude.map(condition =>
            formatSessionAttribute(condition, state.profileProperties)
          )
        );
      }

      if (includeSessionAttributeRules.length > 0) {
        includeRules.sessionAttributes = {
          values: includeSessionAttributeRules,
          matchMode: targeting.sessionAttributes.includeMatchMode
        };
      }
      if (excludeSessionAttributeRules.length > 0) {
        excludeRules.sessionAttributes = {
          values: excludeSessionAttributeRules,
          matchMode: targeting.sessionAttributes.excludeMatchMode
        };
      }
    }

    return { includeRules, excludeRules };
  }, [data, state]);

  if (state.isLoading) {
    return (
      <Box pad='medium' align='center' justify='center' height='medium'>
        <Spinner size='medium' />
        <Text margin={{ top: 'medium' }}>Loading ad group details...</Text>
      </Box>
    );
  }

  const { includeRules, excludeRules } = formatTargetingRules();

  const hasTargeting =
    Object.values(includeRules).some(
      rule => rule.values && rule.values.length > 0
    ) ||
    Object.values(excludeRules).some(
      rule => rule.values && rule.values.length > 0
    );

  return (
    <Box pad='medium' gap='small' background='brand300'>
      {!hasTargeting ? (
        <Box align='center' justify='center'>
          <Text>No Targeting Selected</Text>
        </Box>
      ) : (
        <Box gap='xsmall'>
          <TargetingSection title='Include' rules={includeRules} />
          <TargetingSection title='Exclude' rules={excludeRules} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(AdGroupDetails);
